import { IF, NO, UIDialogView, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHFileUploadView } from "../../Custom components/RH/RHFileUploadView"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { formatRHDateTime } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHDialogs } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { ProcurementQuestionAndAnswerThread } from "../../SocketClientFunctions"
import { RHQuestionNumberFormatter } from "./RHQuestionNumberFormatter"


export class RHQuestionDialogView extends UIDialogView {
    
    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHDialogs.questionDialogBackgroundColor,
            style: {
                borderRadius: RHDialogs.questionDialogBorderRadius,
                boxShadow: RHDialogs.questionDialogBoxShadow,
                width: "90vw"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly titleLabel = new UITextView(undefined, RHDialogs.questionTitleTextViewType)
        .configuredWithObject({
            text: "Teabevahetus",
            textColor: RHDialogs.questionTitleTextColor
        })
        .addedAsSubviewToView(this.contentView)
    
    // Combined header line shown directly above the question bubble:
    // "K\u00fcsimus nr N | <\u00e4rinimi> (<e-post>) | <ajatempel>".
    readonly questionMetadataLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            style: {
                fontSize: RHDialogs.questionMetadataLabelFontSize,
                fontWeight: RHDialogs.questionMetadataLabelFontWeight,
                fontFamily: RHDialogs.questionMetadataLabelFontFamily,
                margin: "0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: RHDialogs.questionMetadataLabelTextColor.stringValue
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly questionBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this.contentView)
    
    readonly questionFilesListView = new RHFileListView()
        .addedAsSubviewToView(this.contentView)
    
    readonly answerSectionLabel = new UITextView(undefined, RHDialogs.questionAnswerSectionTextViewType)
        .configuredWithObject({
            text: "Vastus",
            textColor: RHDialogs.questionAnswerSectionTextColor,
            style: {
                fontWeight: "600",
                margin: "0"
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly answerTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Sisesta vastus siia"
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly answerFileUploadView = new RHFileUploadView()
        .configuredWithObject({
            variant: RHFileUploadView.variant.full,
            bulkDownloadEnabled: NO
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Saada vastus",
                style: {
                    fontSize: RHDialogs.questionFooterButtonFontSize
                }
            },
            variant: RHDialogs.questionSubmitButtonVariant,
            contentPadding: RHDialogs.questionFooterButtonContentPadding,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async () => {
                    await this.submitAnswer()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Sulge",
                style: {
                    fontSize: RHDialogs.questionFooterButtonFontSize
                }
            },
            variant: RHDialogs.questionCancelButtonVariant,
            contentPadding: RHDialogs.questionFooterButtonContentPadding,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.answerTextArea.superview === this.contentView) {
            items.push(this.answerTextArea)
        }
        if (this.cancelButton.superview === this.contentView) {
            items.push(this.cancelButton)
        }
        if (this.submitButton.superview === this.contentView) {
            items.push(this.submitButton)
        }
        return items
    })
    
    private _question?: ProcurementQuestionAndAnswerThread
    private _procurement?: Omit<RHProcurementPublic, "procuringCompany">
    private _onAnswerSubmitted?: (answer?: string) => void
    private _isAnswered: boolean = NO
    private _textAreaHasFocus = false
    
    readonly _escKeyHandler = (event: KeyboardEvent) => {
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Escape") {
            const hasText = this.answerTextArea.text.trim().length > 0
            if (!hasText) {
                this.dismiss()
            }
            else if (!this._textAreaHasFocus) {
                const shower = CBDialogViewShower.showQuestionDialog(
                    "Kas soovid vastuse akna sulgeda? Sisestatud tekst l\u00e4heb kaotsi."
                )
                shower.yesButtonWasPressed = () => {
                    shower.dialogView.dismiss()
                    this.dismiss()
                }
            }
        }
    }
    
    readonly _cmdEnterKeyHandler = (event: KeyboardEvent) => {
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            this.submitAnswer()
        }
    }
    
    // Spacer at the very bottom of the scroll content so the footer buttons keep
    // breathing room above the scroll edge once the content scrolls.
    _bottomView = new UIView().addedAsSubviewToView(this.contentView)
    
    
    constructor(elementID?: string) {
        super(elementID)
        
        this.view = this.contentView
        this.dismissesOnTapOutside = YES
        
        this.questionBanner.alertType = RHAlertBannerView.type.UnansweredQuestion
        this.questionBanner._pointerPosition = "left-edge-top"
        
        this.answerTextArea.viewHTMLElement.addEventListener("focus", () => {
            this._textAreaHasFocus = true
        })
        this.answerTextArea.viewHTMLElement.addEventListener("blur", () => {
            this._textAreaHasFocus = false
        })
    }
    
    
    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        document.body.addEventListener("keydown", this._escKeyHandler)
        document.body.addEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }
    
    override dismiss(animated?: boolean) {
        document.body.removeEventListener("keydown", this._escKeyHandler)
        document.body.removeEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.popLayer()
        super.dismiss(animated)
    }
    
    showForQuestion(
        question: ProcurementQuestionAndAnswerThread,
        procurement: Omit<RHProcurementPublic, "procuringCompany">,
        onSubmitted?: (answer?: string) => void
    ) {
        this._question = question
        this._procurement = procurement
        this._onAnswerSubmitted = onSubmitted
        
        if (question.answerText) {
            this._isAnswered = YES
        }
        else {
            this._isAnswered = NO
        }
        
        const questionDate = new Date(question.createdAt)

        // Header line above the bubble: question number, business name (with the
        // asker's email in parentheses when available), and the question timestamp.
        // The business name is the convention everywhere a sender is identified.
        const questionNumberText = RHQuestionNumberFormatter.metadataTextForQuestion(question)
        const senderCompanyName = question.authorCompanyName || "Tundmatu pakkuja"
        let senderText = senderCompanyName
        if (question.authorName) {
            senderText = `${senderCompanyName} (${question.authorName})`
        }
        this.questionMetadataLabel.text =
            `<b>${questionNumberText}</b> | ${senderText} | ${this._formattedDateTime(questionDate)}`

        this.questionBanner.text = question.bodyText || ""
        this.answerTextArea.text = question.answerText || ""
        
        // Populate question file attachments (read-only list). When the question
        // has no files the list must be detached, not just cleared — an attached
        // but unframed RHFileListView renders its "Faili ei ole lisatud"
        // placeholder at the dialog's top-left corner. QuestionAnswerPairView uses
        // this same remove / re-add pattern.
        if (question.bodyFileIds.length > 0) {
            this.questionFilesListView.addedAsSubviewToView(this.contentView)
            this.questionFilesListView.downloadEnabled = YES
            this.questionFilesListView.deleteEnabled = NO
            this.questionFilesListView.loadFilesWithIds(question.bodyFileIds)
        }
        else {
            this.questionFilesListView.removeFromSuperview()
        }
        
        // Reset answer file uploader for each showing
        this.answerFileUploadView.clearFiles()
        
        if (this._isAnswered) {
            this.questionBanner.alertType = RHAlertBannerView.type.AnsweredQuestion
        }
        else {
            this.questionBanner.alertType = RHAlertBannerView.type.UnansweredQuestion
        }
        
        this.setNeedsLayout()
        
        if (this._isAnswered) {
            this.submitButton.titleLabel.text = "Uuenda vastust"
        }
        else {
            this.submitButton.titleLabel.text = "Saada vastus"
        }
        
        this.showInRootView(YES)
    }
    
    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        this.answerTextArea.focus()
    }
    
    private async submitAnswer() {
        const answerText = this.answerTextArea.text.trim()
        
        if (!answerText) {
            CBDialogViewShower.alert("Palun sisesta vastus")
            return
        }
        
        if (!this._question || !this._question._id) {
            CBDialogViewShower.alert("Viga: k\u00fcsimus pole m\u00e4\u00e4ratud")
            return
        }
        
        try {
            let procurementId: string | undefined
            if (this._procurement?._id) {
                procurementId = this._procurement._id + ""
            }
            const question = await RHCore.sharedInstance.qaStore.answerQuestion({
                questionId: this._question._id,
                answerText: answerText,
                answerFileIds: this.answerFileUploadView.uploadedFiles.map(uploadedFile => uploadedFile.id),
                procurementId
            })
            if (this._onAnswerSubmitted) {
                this._onAnswerSubmitted(question.answerText)
            }
            
            this.dismiss()

        }
        catch (error) {
            console.error("Error submitting answer:", error)
            CBDialogViewShower.alert(
                "Viga vastuse esitamisel: " + (error instanceof Error ? error.message : String(error))
            )
        }
    }
    
    private _formattedDateTime(date: Date): string {
        return formatRHDateTime(date)
    }
    
    private _footerButtonWidth(button: RHButton): number {
        const preferredWidth =
            button.titleLabel.intrinsicContentWidth(RHDialogs.questionFooterButtonHeight) +
            RHDialogs.questionFooterButtonContentPadding * 2 +
            RHDialogs.questionFooterButtonTextSafetyBuffer
        
        return preferredWidth.constrainedValue(
            RHDialogs.questionFooterButtonMinWidth,
            RHDialogs.questionFooterButtonMaxWidth
        )
    }
    
    private _questionBubbleWidth(boundsWidth: number): number {
        return Math.min(
            RHDialogs.questionQuestionBubbleMaxWidth,
            boundsWidth * RHDialogs.questionQuestionBubbleWidthRatio
        )
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const superBounds = this.superview?.bounds ?? this.bounds
        const dialogWidth = Math.min(
            RHDialogs.questionDialogMaxWidth,
            superBounds.width * RHDialogs.questionDialogWidthViewportRatio
        )
        
        this.contentView.frame = superBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)
        
        const bounds = this.contentView.contentBoundsWithInset(padding * 1.5)
        const metadataRowFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(
                RHDialogs.questionTitleBottomGap,
                RHDialogs.questionMetadataRowHeight
            )
            .assignedAsFrameOfView(this.questionMetadataLabel)

        const questionBannerFrame = metadataRowFrame
            .rectangleWithWidth(this._questionBubbleWidth(bounds.width), 0)
            .rectangleForNextRow(RHDialogs.questionQuestionBubbleTopGap, this.questionBanner)
            .assignedAsFrameOfView(this.questionBanner)
        
        // Question file attachments — read-only list, laid out only while attached
        // (empty lists are detached in showForQuestion, see the note there)
        const questionFilesFrame = IF(this.questionFilesListView.superview === this.contentView)(() =>
            questionBannerFrame
                .rectangleWithWidth(bounds.width, 0)
                .rectangleForNextRow(RHDialogs.questionAnswerSectionTopGap, this.questionFilesListView)
                .assignedAsFrameOfView(this.questionFilesListView)
        ).ELSE(() =>
            questionBannerFrame.rectangleWithWidth(bounds.width, 0)
        )
        
        const lastContentFrame = questionFilesFrame
            .rectangleForNextRow(RHDialogs.questionAnswerSectionTopGap, this.answerSectionLabel)
            .assignedAsFrameOfView(this.answerSectionLabel)
            .rectangleForNextRow(
                RHDialogs.questionAnswerTextAreaTopGap,
                RHDialogs.questionAnswerTextAreaHeight
            )
            .assignedAsFrameOfView(this.answerTextArea)
            .rectangleForNextRow(
                RHDialogs.questionAnswerTextAreaTopGap,
                this.answerFileUploadView
            )
            .assignedAsFrameOfView(this.answerFileUploadView)
        
        lastContentFrame
            .rectangleForNextRow(
                RHDialogs.questionFooterButtonsTopGap,
                RHDialogs.questionFooterButtonHeight
            )
            .framesByFlowingViewsAlongWidth(
                [this.cancelButton, this.submitButton],
                [this._footerButtonWidth(this.cancelButton), this._footerButtonWidth(this.submitButton)],
                RHDialogs.questionFooterButtonHeight,
                {
                    itemGap: RHDialogs.questionFooterButtonGap,
                    lineGap: RHDialogs.questionFooterButtonGap,
                    centeredOnPosition: 0.5,
                    positionOrStretchWhenIsolatedOnOwnWrappedRow: "stretch"
                }
            )
            .boundingBox()
            .rectangleForNextRow(0, RHDialogs.questionFooterBottomPadding)
            .assignedAsFrameOfView(this._bottomView)
        const availableHeight = this._fillsViewport
                                ? window.innerHeight / UIView.pageScale
                                : superBounds.height
        
        // Cap the dialog to the viewport so the contentView scroll view takes over
        // when a long question and/or answer make the content taller than the
        // screen. Without the cap the content overflowed above and below the
        // viewport with no way to scroll. The padding * 3 margin mirrors the
        // padding * 1.5 content inset on top and bottom.
        const finalHeight = Math.min(this._bottomView.frame.max.y, availableHeight - padding * 3)
        
        this.contentView.frame = superBounds
            .rectangleWithHeight(availableHeight)
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
    }
    
}
